import { Room } from "src/app/models/Chat.v2";
import db from "src/db";
import { getClientId, getUserId } from "src/app/modules/localstorage";
import { auditChatRead, auditDocIds } from "../utils/firestoreReadAudit";
import { ROOM_CACHE_TTL_MS } from "../config/queryLimits";
import {
  buildChatCacheKey,
  deserializeTimestamp,
  readFreshChatCache,
  serializeTimestamp,
  writeFreshChatCache,
} from "./chatStorageCache";

type RoomCacheEntry = {
  rooms: Room[];
  fetchedAt: number;
};

const roomCache = new Map<string, RoomCacheEntry>();
const roomFetchInFlight = new Map<string, Promise<Room[]>>();
const TIMESTAMP_FIELDS = [
  "createdAt",
  "updatedAt",
  "lastCustomerInteractionAt",
] as const;

const isFresh = (entry?: RoomCacheEntry): entry is RoomCacheEntry => {
  return Boolean(entry && Date.now() - entry.fetchedAt < ROOM_CACHE_TTL_MS);
};

const auditRoomCacheHit = (
  collabId: string,
  source: string,
  rooms: Room[],
  cacheState: "hit" | "inflight" | "localstorage_hit"
) => {
  auditChatRead({
    event: "rooms_fetch",
    source: `${source}_${cacheState}`,
    collaborationId: collabId,
    docs: rooms.length,
    docIds: auditDocIds(rooms.map((room) => room.id)),
    estimatedReads: 0,
    details: {
      cacheState,
      ttlMs: ROOM_CACHE_TTL_MS,
    },
  });
};

export const buildRoomStorageCacheKey = (collabId: string) => {
  const clientId = getClientId();
  const userId = getUserId();

  if (!clientId || !userId || !collabId) return null;
  return buildChatCacheKey(clientId, userId, "rooms", collabId);
};

const serializeRef = (value: any) => {
  if (!value) return value;
  if (typeof value !== "object") return value;
  return {
    id: value.id,
    path: value.path,
  };
};

const serializeRoomForStorage = (room: Room) => {
  const serialized: Record<string, any> = {
    ...room,
    account: serializeRef(room.account),
    client: serializeRef(room.client),
    toUser: room.toUser?.map(serializeRef),
    toUserOnline: room.toUserOnline?.map(serializeRef),
  };

  TIMESTAMP_FIELDS.forEach((field) => {
    serialized[field] = serializeTimestamp(room[field]);
  });

  return serialized;
};

const hydrateRoomFromStorage = (room: Record<string, any>) => {
  const hydrated = { ...room };

  TIMESTAMP_FIELDS.forEach((field) => {
    hydrated[field] = deserializeTimestamp(room[field]);
  });

  return hydrated as Room;
};

const readStoredRooms = (collabId: string) => {
  const key = buildRoomStorageCacheKey(collabId);
  if (!key) return null;

  const cached = readFreshChatCache<Array<Record<string, any>>>(
    key,
    ROOM_CACHE_TTL_MS
  );

  if (!cached) return null;

  return cached.value.map(hydrateRoomFromStorage);
};

const writeStoredRooms = (collabId: string, rooms: Room[]) => {
  const key = buildRoomStorageCacheKey(collabId);
  if (!key) return false;

  return writeFreshChatCache(
    key,
    rooms.map((room) => serializeRoomForStorage(room))
  );
};

/**
 * Fetches all rooms for a given collaboration
 * @param collabId - The collaboration ID
 * @returns Promise<Room[]> - Array of rooms
 */
export const fetchRooms = async (
  collabId: string,
  source = "unknown"
): Promise<Room[]> => {
  if (!collabId) return [];

  const cached = roomCache.get(collabId);
  if (isFresh(cached)) {
    auditRoomCacheHit(collabId, source, cached.rooms, "hit");
    return cached.rooms;
  }

  const storedRooms = readStoredRooms(collabId);
  if (storedRooms) {
    roomCache.set(collabId, { rooms: storedRooms, fetchedAt: Date.now() });
    auditRoomCacheHit(collabId, source, storedRooms, "localstorage_hit");
    return storedRooms;
  }

  const inFlight = roomFetchInFlight.get(collabId);
  if (inFlight) {
    const rooms = await inFlight;
    auditRoomCacheHit(collabId, source, rooms, "inflight");
    return rooms;
  }

  const request = (async () => {
    const startedAt = Date.now();
    const snap = await db
      .collection("collaborations")
      .doc(collabId)
      .collection("rooms")
      .get();

    auditChatRead({
      event: "rooms_fetch",
      source,
      collaborationId: collabId,
      docs: snap.docs.length,
      docIds: auditDocIds(snap.docs.map((doc) => doc.id)),
      estimatedReads: snap.docs.length,
      elapsedMs: Date.now() - startedAt,
    });

    const rooms = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Room[];

    roomCache.set(collabId, { rooms, fetchedAt: Date.now() });
    writeStoredRooms(collabId, rooms);
    return rooms;
  })();

  roomFetchInFlight.set(collabId, request);

  try {
    return await request;
  } catch (error) {
    console.error(
      `[fetchRooms] Error fetching rooms for collabId: ${collabId}`,
      error
    );
    return [];
  } finally {
    roomFetchInFlight.delete(collabId);
  }
};

export const clearRoomCache = () => {
  roomCache.clear();
  roomFetchInFlight.clear();
};
